import React, { useState, useCallback } from 'react';
import type { AxiosInstance } from 'axios';
import { GenericTable } from '@ui-components/Table/GenericTable';
import GenericButton from '@ui-components/Button/GenericButton';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import PackFormModal from './PackFormModal';
import type { PackFormData } from './PackFormModal';
import type { FortiFlexPack } from '../hooks/useFortiFlexData';
import { createPack, updatePack, deletePack } from '../request/request';

interface PacksTableProps {
    packs: FortiFlexPack[];
    api: AxiosInstance;
    onMutate: () => void;
}

const STATUS_VARIANT: Record<string, 'success' | 'error' | 'neutral'> = {
    active: 'success',
    expired: 'neutral',
    cancelled: 'error',
};

const PacksTable: React.FC<PacksTableProps> = ({ packs, api, onMutate }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [editPack, setEditPack] = useState<FortiFlexPack | null>(null);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);

    const openCreate = () => {
        setEditPack(null);
        setModalOpen(true);
    };

    const openEdit = (p: FortiFlexPack) => {
        setEditPack(p);
        setModalOpen(true);
    };

    const handleSubmit = useCallback(
        async (data: PackFormData) => {
            setSaving(true);
            try {
                if (editPack) {
                    await updatePack(api, editPack.id, {
                        packIndex: data.packIndex,
                        totalPoints: data.totalPoints,
                        status: data.status,
                        notes: data.notes,
                    });
                } else {
                    await createPack(api, {
                        packNumber: data.packNumber,
                        packIndex: data.packIndex,
                        totalPoints: data.totalPoints,
                        startDate: data.startDate,
                        expirationDate: data.expirationDate,
                        notes: data.notes,
                    });
                }
                setModalOpen(false);
                onMutate();
            } catch {
                // error is visible via network; keep modal open
            } finally {
                setSaving(false);
            }
        },
        [api, editPack, onMutate]
    );

    const handleDelete = useCallback(
        async (id: number) => {
            setBusyId(id);
            try {
                await deletePack(api, id);
                onMutate();
            } catch {
                // error visible via network
            } finally {
                setBusyId(null);
            }
        },
        [api, onMutate]
    );

    const columns = [
        { header: 'Pack #', accessor: 'packNumber' as keyof FortiFlexPack, width: '130px' },
        { header: 'Index', accessor: 'packIndex' as keyof FortiFlexPack, width: '80px' },
        {
            header: 'Total Points',
            renderCell: (r: FortiFlexPack) => r.totalPoints.toLocaleString(),
            width: '130px',
        },
        { header: 'Start', accessor: 'startDate' as keyof FortiFlexPack, width: '120px' },
        {
            header: 'Expiration',
            accessor: 'expirationDate' as keyof FortiFlexPack,
            width: '120px',
        },
        {
            header: 'Status',
            renderCell: (r: FortiFlexPack) => (
                <GenericTag
                    label={r.status}
                    variant={STATUS_VARIANT[r.status] || 'neutral'}
                    size="small"
                    rounded="full"
                />
            ),
            width: '100px',
        },
        {
            header: 'Actions',
            width: '150px',
            renderCell: (r: FortiFlexPack) => {
                const busy = busyId === r.id;
                return (
                    <div className="flex items-center gap-2">
                        <GenericButton
                            variant="secondary"
                            size="sm"
                            label="Edit"
                            onClick={() => openEdit(r)}
                            disabled={busy}
                        />
                        <GenericButton
                            variant="danger"
                            size="sm"
                            label="Delete"
                            onClick={() => handleDelete(r.id)}
                            loading={busy}
                            disabled={busy}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-semantic-text-primary">
                    FortiFlex Packs
                </h3>
                <GenericButton
                    variant="brand"
                    size="md"
                    label="Add Pack"
                    onClick={openCreate}
                />
            </div>
            <GenericTable columns={columns} data={packs} initialRowsPerPage={10} />
            <PackFormModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
                pack={editPack}
                loading={saving}
            />
        </div>
    );
};

export default PacksTable;
